import React, { useEffect, useState } from 'react';
import Button from '../../../../../component/ui/Button';
import limitIcon from '../../../../../assets/image/subscription/limits-icon.svg';
import advFeatureIcon from '../../../../../assets/image/subscription/adv-feature-icon.svg';
import priorityIcon from '../../../../../assets/image/subscription/priority-support-icon.svg';
import safeSecureIcon from '../../../../../assets/image/subscription/safe-secure-icon.svg';
import checkIcon from '../../../../../assets/image/check_stock_icon.svg';
import CurrentPlanComponents from './componets/currentPlanComponents';
import PlanBenefit from './componets/planBenefit';
import SubscriptionStatisticsUsages from './componets/subscriptionStatisticsUsages';
import SubscriptionFeatures from './componets/subscriptionFeatures';
import CancelSubscriptionModel from './componets/cancelSubscriptionModel';
import CancelSubscriptionReasonModal from './componets/cancelSubscriptionReasonModal';
import ManageBilling from './manageBilling';
import CustomToastMessage from '../../../../components/customToastMessage';
import {
	getSubscriptions,
	cancelScheduledDowngrade,
} from '../../../../../services/settings/subscriptions';
import PricingModal from '../../../../../component/PricingModal';
import UsageOverviewModal from './componets/usageOverviewModal';
import PageLoader from '../../../../../component/PageLoader';
import { toast } from 'react-toastify';
import { usePayment } from '../../../../../hooks/usePayment';
import { axiosInstanceV2 } from '../../../../../services/axios';

export default function Subscription({ activeTab }) {
	const [isOpenUpgradeModal, setIsOpenUpgradeModal] = useState(false);
	const [isOpenCancelSubscriptionModal, setIsOpenCancelSubscriptionModal] = useState(false);
	const [isOpenReasonModal, setIsOpenReasonModal] = useState(false);
	const [isShowToast, setShowToast] = useState(false);
	const [isloadPlan, setIsLoadPlan] = useState(false);
	const [isOpenUsageOverviewModal, setIsOpenUsageOverviewModal] = useState(false);
	const [isloadSubscription, setIsLoadSubscription] = useState(false);
	const [subscriptionScreenType, setSubscriptionScreenType] = useState('subscription');
	const [planBenefit, setPlanBenefit] = useState([
		{
			icon: limitIcon,
			title: 'Higher Limits',
			description: 'Increase invoices, users, and storage as you grow.',
		},
		{
			icon: advFeatureIcon,
			title: 'Advanced Features',
			description: 'Unlock advanced reports, analytics, and automation.',
		},
		{
			icon: priorityIcon,
			title: 'Priority Support',
			description: 'Get faster response with priority support.',
		},
		{
			icon: safeSecureIcon,
			title: 'Safe & Secure',
			description: 'Your data is protected and backed up.',
		},
	]);
	const [subscriptionDetails, setSubscriptionDetails] = useState({});
	const [loadError, setLoadError] = useState(null);
	const [isCancellingDowngrade, setIsCancellingDowngrade] = useState(false);
	const { openSubscriptionCheckout } = usePayment();
	// Also true when the CURRENT plan is fully active but a SEPARATE
	// upgrade/downgrade replacement is waiting on a new mandate —
	// `subscriptionDetails.status` alone only covers a brand-new subscriber
	// whose only row is unpaid, not this case. `/subscription/pending`, which
	// handleCompletePayment calls, already resolves either kind.
	const isPendingPayment =
		['created', 'authenticated'].includes(subscriptionDetails?.status) ||
		Boolean(subscriptionDetails?.pending_change);

	const getSubscriptionDetails = async () => {
		try {
			setIsLoadSubscription(true);
			setLoadError(null);
			const response = await getSubscriptions();
			// `?? {}` matters: the response can legitimately arrive without a
			// `subscription` key, and storing `undefined` made every later
			// property read on it throw during render.
			setSubscriptionDetails(response?.subscription ?? {});
		} catch (error) {
			// This catch used to be empty, so a failed load left the page to
			// render against missing data and crash with an unhandled TypeError
			// instead of telling the user anything.
			console.error('Failed to load subscription details:', error);
			setLoadError(error);
			setSubscriptionDetails({});
		} finally {
			setIsLoadSubscription(false);
		}
	};

	useEffect(() => {
		getSubscriptionDetails();
	}, []);

	const handleCancelScheduledDowngrade = async () => {
		try {
			setIsCancellingDowngrade(true);
			const result = await cancelScheduledDowngrade();
			// The backend returns success only after re-fetching from Razorpay and
			// confirming the replacement really is terminal, so this message is safe
			// to show as fact rather than as an assumption about what a 200 meant.
			toast.success(result?.message ?? 'Scheduled downgrade cancelled.');
			await getSubscriptionDetails();
		} catch (error) {
			toast.error(error?.message ?? 'Unable to cancel the scheduled downgrade.');
		} finally {
			setIsCancellingDowngrade(false);
		}
	};

	const showMessage = () => {
		setShowToast(true);
		setTimeout(() => {
			setShowToast(false);
		}, 3000);
	};

	// service
	const handleCompletePayment = async () => {
		try {
			const token = localStorage.getItem('authToken');
			const response = await axiosInstanceV2.get('/subscription/pending', {
				headers: { Authorization: `Bearer ${token}` },
			});
			const payload = response.data.data;
			await openSubscriptionCheckout({
				subscription: payload,
				billingDetails: subscriptionDetails.billingDetails,
				// Opens Razorpay on the method they used before; both card and UPI
				// remain selectable, so this no longer locks them to it.
				paymentMethod: subscriptionDetails.payment_details?.method ?? null,
				// Refetch this page's own copy of subscription state once payment is
				// confirmed — resuming a pending payment used to leave this page
				// showing the old data until a manual reload.
				onSuccess: getSubscriptionDetails,
			});
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Unable to resume payment');
		}
	};

	return isloadSubscription ? (
		<PageLoader />
	) : subscriptionScreenType === 'subscription' ? (
		<div>
			<PricingModal
				isOpen={isOpenUpgradeModal}
				handleClose={() => setIsOpenUpgradeModal(false)}
				// This page keeps its own copy of /subscription/details, so a plan
				// change has to refetch it here too. Without this the modal could only
				// refresh Redux, and the scheduled-downgrade banner below would not
				// appear until a manual reload.
				onPlanChanged={getSubscriptionDetails}
			/>
			<div style={{ backgroundColor: '#EDEDF7', position: 'sticky', top: 0, zIndex: 100 }}>
				<div className="d-flex py-3 px-2 justify-content-between align-items-center">
					<div className="fs-3 fw-bold">{'Subscription & Billing'}</div>
					<div className="d-flex gap-3 px-3">
						{isPendingPayment ? (
							<Button
								type="button"
								className="btn-primary fs-5 fw-light py-1 px-4"
								button="Complete Payment"
								onClick={handleCompletePayment}
							/>
						) : (
							<Button
								type="button"
								className="mt-0 btn-primary fs-5 fw-light add-pro py-1"
								button="Upgrade"
								onClick={() => setIsOpenUpgradeModal(true)}
							/>
						)}
					</div>
				</div>
			</div>
			<div style={{ height: 'calc(100vh - 120px)', overflowX: 'auto', overflowY: 'auto' }}>
				{loadError ? (
					// A failed load now renders an explanation instead of letting the
					// components below run against missing data and throw.
					<div className="p-5 text-center">
						<h5 className="mb-2">
							{loadError.isUnauthorized
								? 'Your session has expired'
								: "We couldn't load your subscription"}
						</h5>
						<p
							className="mb-4"
							style={{ color: '#64748B' }}
						>
							{loadError.isUnauthorized
								? 'Please sign in again to view your subscription and billing details.'
								: loadError.isNetworkError
									? 'We could not reach the server. Check your connection and try again.'
									: (loadError.message ?? 'Something went wrong. Please try again.')}
						</p>
						{!loadError.isUnauthorized && (
							<Button
								type="button"
								className="btn-primary fs-5 fw-light py-1 px-4"
								button={isloadSubscription ? 'Retrying…' : 'Try again'}
								onClick={getSubscriptionDetails}
							/>
						)}
					</div>
				) : (
					<div className="p-3">
						{/*
						  The backend has always sent these three fields; nothing rendered
						  them. That is why a downgrade looked like a no-op: it is applied
						  at cycle end by design, so with no banner the page was identical
						  before and after a change that had in fact succeeded.
						*/}
						{subscriptionDetails?.is_downgrade_scheduled && (
							<div
								className="d-flex justify-content-between align-items-center gap-3 p-3 mb-3"
								style={{
									backgroundColor: '#FFF7ED',
									border: '1px solid #FDBA74',
									borderRadius: 8,
								}}
							>
								<div>
									<div className="fw-bold mb-1">Downgrade scheduled</div>
									<div style={{ color: '#7C2D12' }}>
										You&apos;ll move to{' '}
										<strong>{subscriptionDetails.downgrade_plan?.name ?? 'a lower plan'}</strong>
										{subscriptionDetails.downgrade_at
											? ` on ${new Date(subscriptionDetails.downgrade_at).toLocaleDateString()}`
											: ' at the end of your current billing period'}
										. You keep your current plan and its limits until then.
									</div>
								</div>
								{/*
								  Wrapped because Button renders its own outer <div>, so
								  layout classes passed via `className` land on the inner
								  <button> and never reach this flex row.
								*/}
								<div
									className="flex-shrink-0"
									style={{ minWidth: 170 }}
								>
									<Button
										type="button"
										className="btn-primary fs-6 fw-light py-1 px-3"
										button={isCancellingDowngrade ? 'Cancelling…' : 'Cancel downgrade'}
										disabled={isCancellingDowngrade}
										onClick={handleCancelScheduledDowngrade}
									/>
								</div>
							</div>
						)}
						<CurrentPlanComponents
							currentPlan={subscriptionDetails}
							setIsOpenCancelSubscriptionModal={setIsOpenCancelSubscriptionModal}
							setSubscriptionScreenType={setSubscriptionScreenType}
						/>
						<SubscriptionStatisticsUsages
							// Optional chaining: this exact read crashed the page when the
							// state held `undefined` after a failed load.
							planUsages={subscriptionDetails?.plan_usages}
							setIsOpenUsageOverviewModal={setIsOpenUsageOverviewModal}
						/>
						<PlanBenefit benefits={planBenefit} />
						<SubscriptionFeatures
							features={subscriptionDetails?.features}
							loader={isloadPlan}
						/>
					</div>
				)}
			</div>
			{isOpenCancelSubscriptionModal && (
				<CancelSubscriptionModel
					isOpenModal={isOpenCancelSubscriptionModal}
					setIsOpenModal={setIsOpenCancelSubscriptionModal}
					setIsOpenReasonModal={setIsOpenReasonModal}
				/>
			)}
			{isOpenUsageOverviewModal && (
				<UsageOverviewModal
					isOpenModal={isOpenUsageOverviewModal}
					setIsOpenModal={setIsOpenUsageOverviewModal}
					planType={subscriptionDetails?.plan?.name}
					setIsOpenUpgradeModal={setIsOpenUpgradeModal}
				/>
			)}

			{isOpenReasonModal && (
				<CancelSubscriptionReasonModal
					isOpenModal={isOpenReasonModal}
					setIsOpenModal={setIsOpenReasonModal}
					showMessage={showMessage}
					onCancelSuccess={() => {
						getSubscriptionDetails();
					}}
				/>
			)}
			{isShowToast && (
				<CustomToastMessage
					icon={checkIcon}
					title={'Subscription cancelled successfully'}
					description={
						'You can continue using Pharmnex till your current plan ends. You will have 30 days after expiry to export your data or renew your subscription.'
					}
				/>
			)}
		</div>
	) : subscriptionScreenType === 'manage-billing' ? (
		<div style={{ height: 'calc(100vh - 80px)', overflowX: 'hidden', overflowY: 'auto' }}>
			<ManageBilling setSubscriptionScreenType={setSubscriptionScreenType} />
		</div>
	) : (
		<></>
	);
}
